'use client';

import * as React from 'react';
import { DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  WorkflowProvider,
  WorkflowContent,
  WorkflowNavigation,
  type WorkflowStep,
  useWorkflow
} from '@/components/ui/workflow';
import { SendNotificationsStep } from './steps/send-notifications-step';
import { useNotificationWorkflow } from '@/lib/services/notifications/workflow-hooks';
import { NotificationsWorkflowSkeleton } from './notifications-workflow-skeleton';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { NotificationsWorkflowProps } from './types';
import type {
  WorkflowInstitution,
  WorkflowDocument,
  WorkflowSignatory,
  BackendCorrespondence
} from '@/lib/services/correspondences/workflows/workflow-types';
import type { MatterFile } from '@/lib/services/files/types';
import { useMatterFilesService } from '@/lib/services/files/hooks';

// Constants for step indices
const _WORKFLOW_STEPS = {
  SELECT_INSTITUTIONS: 0,
  SEND_NOTIFICATIONS: 1
} as const;

// Component that renders the dialog title based on current step
function WorkflowDialogHeader() {
  const { steps, currentStepIndex } = useWorkflow();
  const currentStep = steps[currentStepIndex];

  if (!currentStep) {
    return null;
  }

  return (
    <>
      <DialogTitle>{currentStep.title}</DialogTitle>
      {currentStep.description && (
        <DialogDescription>{currentStep.description}</DialogDescription>
      )}
    </>
  );
}

// Inner component with workflow context access
function WorkflowNavigationWithBulkCreate({
  matterId: _matterId,
  workflowData: _workflowData,
  refetchWorkflowData: _refetchWorkflowData,
  allSentSuccessfully,
  onComplete
}: {
  matterId: number;
  workflowData: {
    institutions?: WorkflowInstitution[];
    documents?: WorkflowDocument[];
    signatories?: WorkflowSignatory[];
    correspondences?: BackendCorrespondence[];
  };
  refetchWorkflowData: () => Promise<unknown>;
  allSentSuccessfully: boolean;
  onComplete: (data: Record<string, unknown>) => void;
}) {
  return (
    <WorkflowNavigation
      submitLabel="Return to Matter"
      onSubmit={() => onComplete({})}
      disableBack={allSentSuccessfully}
    />
  );
}

export function NotificationsWorkflowWithDialog({
  matterId,
  onComplete
}: NotificationsWorkflowProps) {
  const {
    data,
    isLoading,
    error,
    validation,
    sendNotifications,
    refetchWorkflowData,
    isSending,
    sendError: _sendError,
    sendResults
  } = useNotificationWorkflow(matterId);

  // Fetch real matter files for attachments
  const { files: matterFiles } = useMatterFilesService(matterId);

  // Always use real data, never mock data
  const workflowData = React.useMemo(() => {
    // Use backend document format directly
    const realDocuments: WorkflowDocument[] = matterFiles.map(
      (file: MatterFile) => ({
        id: file.id,
        displayName: file.displayName,
        originalFilename: file.originalFilename || `File ${file.id}`,
        createdAt: file.createdAt,
        updatedAt: file.updatedAt || file.createdAt,
        status: file.status || 'available',
        s3Key: file.s3Key || `/api/files/${file.id}/download`,
        imageS3Key: file.imageS3Key || null,
        imageMimeType: file.imageMimeType || null,
        mimeType: file.mimeType || 'application/pdf',
        fileType:
          typeof file.fileType === 'string'
            ? file.fileType
            : file.fileType?.slug || 'other',
        extractedData: file.extractedData || null,
        isCertifiedCopy: file.isCertifiedCopy || false,
        metadata: file.metadata || null,
        analysisMetadata: file.analysisMetadata || null,
        fileSizeKb: file.fileSizeKb || null,
        deletedAt: file.deletedAt || null
      })
    );

    return data
      ? {
          ...data,
          documents: realDocuments // Replace placeholder docs with real matter files
        }
      : {
          institutions: [],
          documents: realDocuments,
          signatories: []
        };
  }, [data, matterFiles]);

  // Track if all correspondence has been sent successfully
  const allSentSuccessfully = React.useMemo(() => {
    if (!sendResults) return false;
    return sendResults.failed.length === 0 && sendResults.successful.length > 0;
  }, [sendResults]);

  // Validate required props (after all hooks have been called).
  if (!matterId) {
    return (
      <>
        <DialogTitle>Send Notification of Death</DialogTitle>
        <div className="container mx-auto max-w-6xl p-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Missing required matter ID. Please ensure the matter is properly
              configured.
            </AlertDescription>
          </Alert>
        </div>
      </>
    );
  }

  const steps: WorkflowStep[] = [
    {
      id: 'send-notifications',
      title: 'Send Correspondence',
      description: 'Review and send notifications to selected institutions',
      component: (
        <SendNotificationsStep
          initialData={workflowData}
          onSend={sendNotifications}
          onRefreshCorrespondences={async () => {
            await refetchWorkflowData();
          }}
          isSending={isSending}
          sendResults={sendResults}
          matterId={matterId}
        />
      )
    }
  ];

  const handleComplete = (data: Record<string, unknown>) => {
    onComplete?.(data);
  };

  // Show loading state with skeleton
  if (isLoading) {
    return (
      <>
        <DialogTitle>Send Notification of Death</DialogTitle>
        <NotificationsWorkflowSkeleton />
      </>
    );
  }

  // Show error state
  if (error) {
    return (
      <>
        <DialogTitle>Send Notification of Death</DialogTitle>
        <div className="container mx-auto max-w-6xl p-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Failed to load workflow data:{' '}
              {error instanceof Error ? error.message : 'Unknown error'}
            </AlertDescription>
          </Alert>
        </div>
      </>
    );
  }

  // Check if we have institutions to notify
  const hasInstitutions = workflowData?.institutions?.length > 0;

  if (!isLoading && !hasInstitutions) {
    return (
      <>
        <DialogTitle>Send Notification of Death</DialogTitle>
        <div className="container mx-auto max-w-6xl p-6">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-2">
                <div className="font-medium">
                  No institutions ready for notification
                </div>
                <p className="text-sm">
                  Please add institutions to the matter's inventory first, then
                  return here to send notifications.
                </p>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      </>
    );
  }

  return (
    <WorkflowProvider steps={steps} onComplete={handleComplete}>
      <div className="space-y-6">
        {/* Render the dialog title based on current step */}
        <WorkflowDialogHeader />

        <div className="container mx-auto max-w-6xl space-y-8">
          {/* Show blocking errors if any */}
          {validation.blockingErrors.length > 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <div className="space-y-1">
                  <div className="font-medium">
                    The following issues must be resolved:
                  </div>
                  <ul className="list-inside list-disc text-sm">
                    {validation.blockingErrors.map((error) => (
                      <li key={error}>{error}</li>
                    ))}
                  </ul>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Hide step title since we're showing it in DialogTitle */}
          <WorkflowContent hideStepTitle />
          <WorkflowNavigationWithBulkCreate
            matterId={matterId}
            workflowData={workflowData}
            refetchWorkflowData={refetchWorkflowData}
            allSentSuccessfully={allSentSuccessfully}
            onComplete={handleComplete}
          />
        </div>
      </div>
    </WorkflowProvider>
  );
}
